import React, { useState, useEffect } from "react";
import { api, unwrapObject } from "@/lib/api";
import type { Task, TaskPriority, TaskType, TaskRelatedType } from "@/types/task";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CalendarDays,
  Clock,
  Video,
  MapPin,
  Users,
  Plus,
  X,
  AlertCircle,
  Loader2,
} from "lucide-react";

interface MeetingTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialStart?: Date;
  initialEnd?: Date;
  initialAllDay?: boolean;
  onSaved?: (createdTask: Task) => void;
}

export function MeetingTaskDialog({
  open,
  onOpenChange,
  initialStart,
  initialEnd,
  initialAllDay,
  onSaved,
}: MeetingTaskDialogProps) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [taskType, setTaskType] = useState<TaskType>("MEETING");
  const [priority, setPriority] = useState<TaskPriority>("NORMAL");
  const [meetingProvider, setMeetingProvider] = useState("GOOGLE_MEET");
  const [onlineLink, setOnlineLink] = useState("");
  const [location, setLocation] = useState("");
  const [allDay, setAllDay] = useState(false);

  // Start & End datetime-local strings
  const [startDateStr, setStartDateStr] = useState("");
  const [endDateStr, setEndDateStr] = useState("");

  // Related
  const [relatedType, setRelatedType] = useState<TaskRelatedType | "NONE">("NONE");
  const [relatedId, setRelatedId] = useState("");
  const [relatedName, setRelatedName] = useState("");

  // Attendees list
  const [attendees, setAttendees] = useState<Array<{ name: string; email: string }>>([]);
  const [newAttName, setNewAttName] = useState("");
  const [newAttEmail, setNewAttEmail] = useState("");

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Format Date to datetime-local string YYYY-MM-DDTHH:mm
  const toLocalInputValue = (d: Date) => {
    const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
      d.getHours()
    )}:${pad(d.getMinutes())}`;
  };

  useEffect(() => {
    if (open) {
      const now = initialStart || new Date();
      // Round to nearest 15 mins
      now.setMinutes(Math.ceil(now.getMinutes() / 15) * 15, 0, 0);
      const end = initialEnd || new Date(now.getTime() + 30 * 60 * 1000);

      setStartDateStr(toLocalInputValue(now));
      setEndDateStr(toLocalInputValue(end));
      setAllDay(Boolean(initialAllDay));
      setTitle("");
      setDescription("");
      setTaskType("MEETING");
      setPriority("NORMAL");
      setMeetingProvider("GOOGLE_MEET");
      setOnlineLink("");
      setLocation("");
      setRelatedType("NONE");
      setRelatedId("");
      setRelatedName("");
      setAttendees([]);
      setNewAttName("");
      setNewAttEmail("");
      setErrorMsg(null);
    }
  }, [open, initialStart, initialEnd, initialAllDay]);

  const handleAddAttendee = () => {
    const email = newAttEmail.trim();
    if (!email) return;
    if (!email.includes("@")) {
      setErrorMsg("Please enter a valid attendee email address");
      return;
    }
    setAttendees((prev) => [
      ...prev,
      { name: newAttName.trim() || email.split("@")[0] || email, email },
    ]);
    setNewAttName("");
    setNewAttEmail("");
    setErrorMsg(null);
  };

  const handleRemoveAttendee = (idx: number) => {
    setAttendees((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setErrorMsg("Please enter a meeting title");
      return;
    }
    if (!startDateStr) {
      setErrorMsg("Please enter a start time");
      return;
    }

    setLoading(true);
    setErrorMsg(null);

    try {
      const startIso = new Date(startDateStr).toISOString();
      const endIso = endDateStr ? new Date(endDateStr).toISOString() : undefined;

      const payload: any = {
        title: title.trim(),
        description: description.trim() || undefined,
        task_type: taskType,
        priority,
        due_at: startIso,
        end_at: endIso,
        all_day: allDay,
        location: location.trim() || undefined,
        online_link: onlineLink.trim() || undefined,
        meeting_provider: taskType === "MEETING" ? meetingProvider : undefined,
        attendees: attendees.length > 0 ? attendees : undefined,
      };

      if (relatedType !== "NONE" && relatedId.trim()) {
        payload.related_type = relatedType;
        payload.related_id = relatedId.trim();
        payload.related_name = relatedName.trim() || undefined;
      }

      const res = await api.post("/api/Task/Insert", payload);
      const created = unwrapObject<Task>(res);
      onSaved?.(created);
      onOpenChange(false);
    } catch (err: any) {
      console.error("Create meeting error:", err);
      setErrorMsg(err?.message || "Failed to schedule meeting");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-primary/10 text-primary">
              <CalendarDays className="size-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold">
                {taskType === "MEETING" ? "Schedule Meeting & Send Invite" : "Create New Task"}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                Organize follow-up sessions, invite attendees, and generate calendar invites.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {errorMsg && (
          <div className="p-3 rounded-lg bg-danger-soft text-danger text-xs border border-danger/20 flex items-start gap-2">
            <AlertCircle className="size-4 shrink-0 mt-0.5" />
            <div>{errorMsg}</div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 py-1 text-xs">
          {/* Title */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Title / Subject *</Label>
            <Input
              placeholder="e.g. Fabric Sample Review & Proposal Discussion"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="h-9 text-xs"
              required
            />
          </div>

          {/* Type & Priority */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Activity Type</Label>
              <Select value={taskType} onValueChange={(v) => setTaskType(v as TaskType)}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MEETING">Meeting</SelectItem>
                  <SelectItem value="CALL">Phone Call</SelectItem>
                  <SelectItem value="FOLLOW_UP">Follow-up</SelectItem>
                  <SelectItem value="EMAIL">Email</SelectItem>
                  <SelectItem value="WHATSAPP">WhatsApp</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Priority</Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as TaskPriority)}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="HIGH">High Priority</SelectItem>
                  <SelectItem value="NORMAL">Normal</SelectItem>
                  <SelectItem value="LOW">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Dates & Times */}
          <div className="p-3 rounded-xl border border-border bg-card/60 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-xs flex items-center gap-1.5 text-foreground">
                <Clock className="size-3.5 text-primary" /> Schedule & Duration
              </span>
              <div className="flex items-center gap-2">
                <Label htmlFor="allDaySwitch" className="text-[11px] text-muted-foreground">
                  All-day
                </Label>
                <Switch
                  id="allDaySwitch"
                  checked={allDay}
                  onCheckedChange={setAllDay}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Starts At</Label>
                <Input
                  type={allDay ? "date" : "datetime-local"}
                  value={startDateStr}
                  onChange={(e) => setStartDateStr(e.target.value)}
                  className="h-8 text-xs"
                  required
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Ends At</Label>
                <Input
                  type={allDay ? "date" : "datetime-local"}
                  value={endDateStr}
                  onChange={(e) => setEndDateStr(e.target.value)}
                  className="h-8 text-xs"
                />
              </div>
            </div>
          </div>

          {/* Meeting Platform & Link */}
          {taskType === "MEETING" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold flex items-center gap-1">
                  <Video className="size-3.5 text-muted-foreground" /> Platform
                </Label>
                <Select value={meetingProvider} onValueChange={setMeetingProvider}>
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GOOGLE_MEET">Google Meet</SelectItem>
                    <SelectItem value="TEAMS">Microsoft Teams</SelectItem>
                    <SelectItem value="ZOOM">Zoom</SelectItem>
                    <SelectItem value="IN_PERSON">In Person / Office</SelectItem>
                    <SelectItem value="CLIENT_SITE">Client Site</SelectItem>
                    <SelectItem value="OTHER">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Video Link (Optional)</Label>
                <Input
                  placeholder="Paste the meeting link"
                  value={onlineLink}
                  onChange={(e) => setOnlineLink(e.target.value)}
                  className="h-9 text-xs"
                />
              </div>
            </div>
          )}

          {/* Location */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold flex items-center gap-1">
              <MapPin className="size-3.5 text-muted-foreground" /> Location / Room (Optional)
            </Label>
            <Input
              placeholder="e.g. Conference Room A or Client Office, Surat"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="h-9 text-xs"
            />
          </div>

          {/* Attendees */}
          <div className="p-3 rounded-xl border border-border bg-card/60 space-y-2.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold flex items-center gap-1.5">
                <Users className="size-3.5 text-primary" /> Meeting Attendees
              </Label>
              <span className="text-[11px] text-muted-foreground">
                Will receive .ics calendar invite
              </span>
            </div>

            {/* List of current attendees */}
            {attendees.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {attendees.map((att, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-md bg-muted text-[11px] border border-border"
                  >
                    <span className="font-medium text-foreground">{att.name}</span>
                    <span className="text-muted-foreground text-[10px]">({att.email})</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveAttendee(i)}
                      className="text-muted-foreground hover:text-danger rounded p-0.5"
                    >
                      <X className="size-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add attendee form */}
            <div className="flex items-center gap-2">
              <Input
                placeholder="Name"
                value={newAttName}
                onChange={(e) => setNewAttName(e.target.value)}
                className="h-8 text-xs flex-1"
              />
              <Input
                placeholder="email@example.com"
                value={newAttEmail}
                onChange={(e) => setNewAttEmail(e.target.value)}
                className="h-8 text-xs flex-1"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddAttendee();
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddAttendee}
                className="h-8 text-xs px-2.5"
              >
                <Plus className="size-3.5 mr-1" /> Add
              </Button>
            </div>
          </div>

          {/* Agenda / Description */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Agenda & Notes</Label>
            <Textarea
              placeholder="Outline meeting agenda, goals, and preparation requirements..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="text-xs min-h-[70px]"
            />
          </div>

          <DialogFooter className="pt-2 border-t border-border flex items-center justify-between">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="text-xs"
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={loading} className="text-xs">
              {loading ? (
                <Loader2 className="size-3.5 animate-spin mr-1.5" />
              ) : (
                <CalendarDays className="size-3.5 mr-1.5" />
              )}
              {taskType === "MEETING" ? "Schedule & Send Invites" : "Create Task"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
